import { PDFPageProxy, PDFDocumentProxy } from 'pdfjs-dist';
import { Renderer2 } from '@angular/core';
import { Environment } from '../../../../models/environment';
import { PDFAnnotationDataCustom } from '../types';

export type PageCustomAnnotations = {
    a: PDFAnnotationDataCustom,
    b: number[],
    c: PDFPageProxy
};

export class PdfPageManager {
    private readonly dpiRatio = 96 / 72;
    private readonly PDF_MINIMUM_SCALE = 1.5;

    readonly pageNumber: number;

    private _canvas: HTMLCanvasElement;
    private _pageImage: HTMLImageElement;

    private _documentProxy: PDFDocumentProxy;
    private _pageProxy: PDFPageProxy;
    private _annotations: PDFAnnotationDataCustom[] = [];
    private _customAnnotations: PageCustomAnnotations[] = [];

    private _hasSinglePageActive: boolean = false;
    private _hasSetCustomAnnotations = false;

    private _hasRenderedImage = false;
    private _hasRenderedWidget = false;
    
    constructor(
        private _environment: Environment,
        private _renderer: Renderer2,
        public proxy: PDFDocumentProxy, 
        public pageIndex: number,
    ) {
        this._documentProxy = proxy;
        this.pageNumber = pageIndex;
        this._hasSinglePageActive = this._environment?.features.singlePage;
    }

    get documentProxy(): PDFDocumentProxy {
        return this._documentProxy;
    }

    get canvas(): HTMLCanvasElement {
        return this._canvas;
    }

    get pageImage(): HTMLImageElement {
        return this._pageImage;
    }

    get annotations(): PDFAnnotationDataCustom[] {
        return this._annotations;
    }

    get customAnnotations(): PageCustomAnnotations[] {
        return this._customAnnotations;
    }

    get hasSinglePageActive(): boolean {
        return this._hasSinglePageActive;
    }

    get hasRenderedImage(): boolean {
        return this._hasRenderedImage;
    }

    get hasRenderedWidget(): boolean {
        return this._hasRenderedWidget;
    }

    set hasRenderedWidget(hasSet: boolean) {
        this._hasRenderedWidget = hasSet;
    }

    get hasSetCustomAnnotations(): boolean {
        return this._hasSetCustomAnnotations;
    }

    set hasSetCustomAnnotations(hasSet: boolean) {
        this._hasSetCustomAnnotations = hasSet;
    }

    async getPageProxy(pageNum: number): Promise<PDFPageProxy> {
        this._pageProxy = await this._documentProxy.getPage(pageNum);

        return this._pageProxy;
    }

    async getPageAnnotations(): Promise<void> {
        this._annotations = await this._pageProxy.getAnnotations() as unknown as PDFAnnotationDataCustom[];
    }

    handleCustomAnnotations(): void {
        let annotationsIds = {};

        const mod145 = this.annotations.find(res => res.pushButton === true);

        if (mod145) {
            this._annotations = this.annotations.filter(res => !res.fieldName.includes('contador') && !res.fieldName.includes('acc') && !res.pushButton && !res.readOnly);
        }

        if (this.annotations.length > 0) {

            for (let annotation of this.annotations) {
                if (annotation.fieldName?.startsWith('SEL_RDB')) {
                    annotation.checkBox = false;
                }

                const isNotSigFieldType = annotation.fieldType !== 'Sig';
                const isWidgetSubType = annotation.subtype === 'Widget';
                const isNotDuplicated = annotationsIds[annotation.id] === undefined;

                if (isNotSigFieldType && isWidgetSubType && isNotDuplicated) {
                    annotationsIds[annotation.id] = true;
                    const fieldRect = this._pageProxy.getViewport({ scale: this.dpiRatio }).convertToViewportRectangle(annotation.rect);
                    this.customAnnotations.push({ a: annotation, b: fieldRect, c: this._pageProxy });
                }
            }
        }
    }

    async renderPdfPage(pageNum: number): Promise<void> {
        let container: HTMLElement = 
            this.hasSinglePageActive ? 
            this._renderer.selectRootElement(`#page-renderer-wrapper-1`, true):
            this._renderer.selectRootElement(`#page-renderer-wrapper-${pageNum}`, true);

        this._canvas = this._renderer.createElement('canvas');

        const context = this.canvas.getContext('2d');

        try {

            let viewport = this._pageProxy.getViewport({ scale: 1 });

            const scale = Math.max(container.clientWidth / viewport.width, this.PDF_MINIMUM_SCALE);

            viewport = this._pageProxy.getViewport({ scale });

            this.canvas.height = viewport.height;
            this.canvas.width = viewport.width;

            const renderContext = {
                canvasContext: context,
                viewport: viewport,
            };

            await this._pageProxy.render(renderContext).promise;
        }
        catch (e) {
            throw new Error('Something went wrong while trying to get a PDF page.');
        };
    }

    handleImageOnLoad(callback: (img: HTMLImageElement) => void): Promise<void> {
        this._pageImage = this.hasSinglePageActive ? 
            this._renderer.selectRootElement(`#page-renderer-image-1`, true): 
            this._renderer.selectRootElement(`#page-renderer-image-${this.pageNumber}`, true);

        return new Promise((resolve, reject) => {
            let blobUrl: string;

            this.canvas.toBlob((blob) => {
                blobUrl = URL.createObjectURL(blob);
                this._renderer.setAttribute(this.pageImage, 'src', blobUrl);
            }, 'image/jpeg');

            this.pageImage.onload = () => {
                this.imageRenderingCallback();
                callback(this.pageImage);
                URL.revokeObjectURL(blobUrl);
                resolve();
            };

            this.pageImage.onerror = () => reject();
        });
    }

    private imageRenderingCallback(): void {
        const finishRenderingProcess = () => this._hasRenderedImage = true;
        const startRenderingProcess = () => requestAnimationFrame(finishRenderingProcess);

        requestAnimationFrame(startRenderingProcess);
    }
}